/**
 * API-клиент портала uchetkm.ru → api.uchetkm.ru
 * Access token в памяти, refresh — HttpOnly cookie, auth_session — hint для proxy.
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "https://api.uchetkm.ru"
const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN ?? ""

export const AUTH_SESSION_COOKIE = "auth_session"

interface ApiResponse<T = unknown> {
  data?: T
  message?: string
  errors?: Record<string, string[]>
}

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(access: string, remember = true): void {
  accessToken = access
  if (typeof window !== "undefined") {
    setAuthSessionHint(remember)
  }
}

export function clearTokens(): void {
  accessToken = null
  if (typeof window !== "undefined") {
    clearAuthSessionHint()
  }
}

function cookieDomainAttr(): string {
  return COOKIE_DOMAIN ? `domain=${COOKIE_DOMAIN}; ` : ""
}

function cookieSecureAttr(): string {
  return typeof window !== "undefined" && window.location.protocol === "https:"
    ? "Secure; "
    : ""
}

function setAuthSessionHint(remember: boolean): void {
  const maxAge = remember ? 315360000 : 24 * 3600
  document.cookie = `${AUTH_SESSION_COOKIE}=1; path=/; ${cookieDomainAttr()}max-age=${maxAge}; SameSite=Lax; ${cookieSecureAttr()}`
}

function clearAuthSessionHint(): void {
  document.cookie = `${AUTH_SESSION_COOKIE}=; path=/; ${cookieDomainAttr()}expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; ${cookieSecureAttr()}`
  document.cookie = `${AUTH_SESSION_COOKIE}=; path=/; max-age=0; SameSite=Lax`
}

export function hasAuthSessionHint(): boolean {
  if (typeof document === "undefined") return false
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${AUTH_SESSION_COOKIE}=`))
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
        cache: "no-store",
      })

      if (response.ok) {
        const data = await response.json()
        if (typeof data.access_token === "string") {
          setAccessToken(data.access_token, hasAuthSessionHint())
          return true
        }
      }
    } catch {
      // refresh failed
    }

    clearTokens()
    return false
  })()

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

export async function restoreSession(): Promise<boolean> {
  if (accessToken) {
    return true
  }
  if (!hasAuthSessionHint()) {
    return false
  }
  return refreshAccessToken()
}

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const isFormData = options.body instanceof FormData
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  }

  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json"
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`
  const isAuthLogin = endpoint.includes("/auth/login")
  const isAuthRefresh = endpoint.includes("/auth/refresh")

  let response = await fetch(url, {
    cache: "no-store",
    ...options,
    headers,
    credentials: "include",
  })

  if (response.status === 401 && !isAuthLogin && !isAuthRefresh) {
    const refreshed = await refreshAccessToken()
    if (refreshed && accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
      response = await fetch(url, {
        cache: "no-store",
        ...options,
        headers,
        credentials: "include",
      })
    }
  }

  if (response.status === 401 && !isAuthLogin && !isAuthRefresh) {
    clearTokens()
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("api:unauthorized"))
    }
    throw new Error("Сессия истекла, выполните вход заново")
  }

  const json = await response.json().catch(() => null)

  if (!response.ok && response.status !== 422) {
    const errMsg = json?.message ?? json?.error
    throw new Error(typeof errMsg === "string" ? errMsg : `HTTP ${response.status}`)
  }

  if (!json) {
    return {} as ApiResponse<T>
  }

  if (typeof json === "object" && !("data" in json)) {
    return {
      data: json as T,
      ...json,
    } as ApiResponse<T>
  }

  return json as ApiResponse<T>
}

export const api = {
  get: <T>(url: string, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: "GET" }),
  post: <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, {
      ...opts,
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  put: <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, {
      ...opts,
      method: "PUT",
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  patch: <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(url: string, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: "DELETE" }),
}
